// FECHAMENTO-CC (2026-08-17): ranking de lucro por contrato.
//
// Cada linha vem da soma dos itens do fato (financeiro.dre_contrato_itens) no
// período. A soma de todos os contratos + a linha "empresa" (o que não é de
// contrato nenhum: investimentos, financeiro, overhead sem receita para
// ratear) reproduz a DRE geral — é o que "reconciliação ok" quer dizer.
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Drawer, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { DreContratoView, brl } from '../components/DreContratoView';

const { Text, Title } = Typography;

async function api(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts, headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  let d: any = await r.json().catch(() => ({}));
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') { try { d = JSON.parse(d.body); } catch {} }
  if (!r.ok || d?.erro) throw new Error(d?.erro || `Erro ${r.status}`);
  return d;
}

type Linha = {
  contrato_id: string | null; numero?: string; titulo?: string; cliente?: string; status?: string; empresa: boolean;
  receita: number; deducao: number; comissao: number; custo_direto: number; overhead: number; imposto: number; residual: number;
  direto_por_origem: Record<string, number>;
  margem_contribuicao: number; margem_contribuicao_pct: number | null; lucro: number; lucro_pct: number | null;
};

const pct = (v: number | null) => v == null ? '—' : `${v.toFixed(1)}%`;
const corMargem = (v: number | null) => v == null ? undefined : v < 0 ? '#a8071a' : v < 15 ? '#ad6800' : '#237804';

export default function ResultadoContratos() {
  const ano = new Date().getFullYear();
  const [de, setDe] = useState<Dayjs>(dayjs(`${ano}-01-01`));
  const [ate, setAte] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [dados, setDados] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<Linha | null>(null);
  const [recalculando, setRecalculando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const d = await api(`/financeiro/resultado-contratos?de=${de.format('YYYY/MM')}&ate=${ate.format('YYYY/MM')}`);
      setDados(d);
    } catch (e: any) { setErro(e.message); }
    finally { setLoading(false); }
  }, [de, ate]);
  useEffect(() => { carregar(); }, [carregar]);

  async function recalcularFaltantes() {
    if (!dados) return;
    setRecalculando(true);
    try {
      // Até 6 por clique (cada mês roda a DRE geral inteira, alguns segundos)
      // e segue no próximo se um falhar — o botão continua mostrando o que falta.
      const meses: string[] = (dados.meses_sem_fechamento || []).slice(-6);
      const falhas: string[] = [];
      for (const m of meses) {
        try { await api('/financeiro/fechamento-cc/recalcular', { method: 'POST', body: JSON.stringify({ competencia: m }) }); }
        catch (e: any) { falhas.push(`${m}: ${e.message}`); }
      }
      await carregar();
      if (falhas.length) setErro('Não calculou: ' + falhas.join(' · '));
    } catch (e: any) { setErro('Recalcular: ' + e.message); }
    finally { setRecalculando(false); }
  }

  const contratos: Linha[] = dados?.contratos || [];
  const empresa: Linha | null = dados?.empresa || null;

  return (
    <div style={{ padding: 16 }}>
      <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>Resultado por contrato</Title>
        <Space wrap>
          <DatePicker picker="month" value={de} onChange={(v) => v && setDe(v)} format="MM/YYYY" allowClear={false} />
          <Text type="secondary">a</Text>
          <DatePicker picker="month" value={ate} onChange={(v) => v && setAte(v)} format="MM/YYYY" allowClear={false} />
          {dados?.meses_sem_fechamento?.length > 0 && (
            <Tooltip title={`Calcular: ${dados.meses_sem_fechamento.join(', ')}`}>
              <Button icon={<ReloadOutlined />} loading={recalculando} onClick={recalcularFaltantes}>
                Calcular {dados.meses_sem_fechamento.length} mês(es)
              </Button>
            </Tooltip>
          )}
        </Space>
      </Space>

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Lucro = receita − custo direto (despesas no CC, mão de obra por horas, equipamento por dias) − overhead rateado por receita − impostos do lucro presumido."
        description={dados?.reconciliacao_ok === false
          ? 'Atenção: um ou mais meses não reconciliam com a DRE geral — o total abaixo pode não bater com a DRE da empresa.'
          : 'A soma de todos os contratos mais a linha "empresa" reproduz a DRE geral do período, centavo a centavo.'} />
      {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 12 }} />}

      <Table size="small" rowKey={(r: Linha) => r.contrato_id || 'empresa'} loading={loading} pagination={false}
        dataSource={contratos} scroll={{ x: 1100 }}
        onRow={(r) => ({ onClick: () => setAberto(r), style: { cursor: 'pointer' } })}
        summary={() => {
          const t = dados?.totais || { receita: 0, lucro: 0 };
          return (
            <Table.Summary fixed>
              {empresa && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}><Text type="secondary">Empresa (não é de contrato nenhum)</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">{brl(empresa.receita)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{brl(empresa.custo_direto)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">—</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">{brl(empresa.overhead + empresa.residual)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">{brl(empresa.deducao + empresa.imposto)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">{brl(empresa.lucro)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">—</Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}><b>Total (= DRE geral)</b></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><b>{brl(t.receita)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={4} />
                <Table.Summary.Cell index={7} align="right"><b>{brl(t.lucro)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right"><b>{t.receita ? pct(t.lucro / t.receita * 100) : '—'}</b></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
        columns={[
          { title: 'Contrato', key: 'c', width: 260, ellipsis: true, render: (_: any, r: Linha) => (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.titulo}>
                {r.numero || '—'} · {r.titulo || ''}
              </div>
              <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cliente || ''}</div>
            </div>
          ) },
          { title: 'Status', dataIndex: 'status', width: 100, render: (v: string) => v ? <Tag>{v}</Tag> : null },
          { title: 'Receita', dataIndex: 'receita', width: 120, align: 'right' as const, render: brl, sorter: (a: Linha, b: Linha) => a.receita - b.receita },
          { title: 'Custo direto', dataIndex: 'custo_direto', width: 120, align: 'right' as const, render: (v: number, r: Linha) => (
            <Tooltip title={Object.entries(r.direto_por_origem || {}).map(([k, x]) => `${k}: ${brl(x)}`).join(' · ') || 'nada direto'}>
              <span>{brl(v)}</span>
            </Tooltip>
          ) },
          { title: 'Margem contrib.', key: 'mc', width: 130, align: 'right' as const,
            sorter: (a: Linha, b: Linha) => (a.margem_contribuicao_pct ?? -999) - (b.margem_contribuicao_pct ?? -999),
            render: (_: any, r: Linha) => <span style={{ color: corMargem(r.margem_contribuicao_pct) }}>{brl(r.margem_contribuicao)} <Text type="secondary" style={{ fontSize: 11 }}>({pct(r.margem_contribuicao_pct)})</Text></span> },
          { title: 'Overhead', dataIndex: 'overhead', width: 110, align: 'right' as const, render: brl },
          { title: 'Impostos', key: 'imp', width: 110, align: 'right' as const, render: (_: any, r: Linha) => brl(r.deducao + r.imposto) },
          { title: 'Lucro', dataIndex: 'lucro', width: 130, align: 'right' as const, defaultSortOrder: 'descend' as const,
            sorter: (a: Linha, b: Linha) => a.lucro - b.lucro,
            render: (v: number) => <b style={{ color: v >= 0 ? '#237804' : '#a8071a' }}>{brl(v)}</b> },
          { title: '%', dataIndex: 'lucro_pct', width: 70, align: 'right' as const, render: (v: number | null) => <span style={{ color: corMargem(v) }}>{pct(v)}</span> },
        ]} />

      <Drawer open={!!aberto} onClose={() => setAberto(null)} width="min(860px, 96vw)"
        title={aberto ? `${aberto.numero || ''} · ${aberto.titulo || ''}` : ''}>
        {aberto?.contrato_id && <DreContratoView contratoId={aberto.contrato_id} />}
      </Drawer>
    </div>
  );
}
